import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { crumbsFor, entityLabel, modulesFor } from "../lib/nav";

/** Breadcrumb bar under the header (requirement 9.1): Home › Module › Entity › Record, from the navigation registry, with
 * the module menu on the right for a direct jump. Hidden on the landing page, which is the hub itself. */
export function Breadcrumbs({ record }: { record?: string }) {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const { identity } = useAuth();
  const crumbs = crumbsFor(pathname, search, i18n.language);
  const last = crumbs[crumbs.length - 1];
  if (record && last?.literal && !last.to) crumbs[crumbs.length - 1] = { label: record, literal: true };
  const modules = modulesFor(identity?.roles ?? []);
  const closeMenu = (e: React.MouseEvent) => { const d = (e.currentTarget as HTMLElement).closest("details"); if (d) d.open = false; };
  return (
    <nav aria-label="breadcrumb" data-breadcrumbs className="border-b border-brand-grey-light bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-1.5 text-xs">
        <ol className="flex min-w-0 flex-wrap items-center gap-1 text-brand-grey">
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            const label = c.literal ? c.label : t(c.label);
            const text = i === 0 ? `⌂ ${label}` : label;
            return (
              <li key={`${i}-${c.label}`} className="flex items-center gap-1" data-crumb={i}>
                {i > 0 && <span aria-hidden className="px-0.5 text-brand-grey-light">›</span>}
                {isLast || !c.to
                  ? <span aria-current={isLast ? "page" : undefined} className={isLast ? "font-semibold text-brand-charcoal" : ""}>{text}</span>
                  : <Link to={c.to} className="hover:text-brand-primary hover:underline">{text}</Link>}
              </li>
            );
          })}
        </ol>
        <details className="relative shrink-0" data-module-menu>
          <summary className="cursor-pointer list-none rounded border border-brand-grey-light px-2 py-1 font-semibold text-brand-charcoal hover:border-brand-primary hover:text-brand-primary">☰ {t("nav.modules")}</summary>
          <div className="absolute right-0 z-20 mt-1 grid w-[40rem] max-w-[90vw] grid-cols-1 gap-x-4 gap-y-2 rounded-lg border border-brand-grey-light bg-white p-3 shadow-lg sm:grid-cols-2">
            {modules.map((m) => (
              <div key={m.key} className="min-w-0">
                <Link to={m.to} onClick={closeMenu} className="font-semibold text-brand-charcoal hover:text-brand-primary hover:underline">{t(m.label)}</Link>
                {m.entities.length > 0 && (
                  <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-brand-grey">
                    {m.entities.slice(0, 6).map((e) => <Link key={e.key} to={e.to} onClick={closeMenu} className="hover:text-brand-primary hover:underline">{entityLabel(e, i18n.language, t)}</Link>)}
                    {m.entities.length > 6 && <Link to={m.to} onClick={closeMenu} className="text-brand-primary">+{m.entities.length - 6}</Link>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      </div>
    </nav>
  );
}
